import { FC, memo } from 'react';
import { FaCheck } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';
import Image from 'next/image';

import { SafeType } from '@benzinga/safe-await';
import { Authentication } from '@benzinga/session';
import { Register } from '../../Register';
import { AuthIterationType } from '../types';
import { buildCheckoutUrl, getPaywallConfig, handlePaywallLoginRedirect } from '../../../utils/paywallUtils';
import { usePaywallTracking } from '../../../hooks/usePaywallTracking';
import styles from '../paywall.module.scss';

import { EdgeLogoSvg } from '../SophiIteration';

interface OptimizelyRegPaywallProps extends AuthIterationType {
  isMobile?: boolean;
  paywallId?: string;
  onRegister?: (auth: SafeType<Authentication>, authFor: string) => void;
}

export const OptimizelyRegPaywall: FC<OptimizelyRegPaywallProps> = memo(
  ({ allowClose, isMobile, onRegister, placement, setShowPaywall }) => {
    const adType = allowClose ? 'soft' : 'hard';
    const adScreen = isMobile ? 'mo' : 'de';
    const adId = `${adType}-${adScreen}-optimizely-reg`;
    const paywallId = 'optimizely-reg';
    const { utmSource } = getPaywallConfig(paywallId, {}, 'optimizely-reg', 'optimizely-reg');

    const campaign = 'command-center-optimizely-reg';

    const checkoutUrl = buildCheckoutUrl({
      ad: adId,
      adType: 'regwall',
      campaign,
    });

    const listItems = [
      'Unmatched, global, round-the-clock news',
      'Hand-picked insights that are easy to find and use',
      "Our Daily Briefing newsletter, delivering the day's headline right to your inbox",
    ];

    usePaywallTracking({
      allowClose,
      checkoutUrl,
      isRegwall: true,
      placement,
      utmSource,
    });

    const handleLoginRedirect = () => handlePaywallLoginRedirect(utmSource, 'login', 'regwall');

    return (
      <>
        <div className="fixed inset-0 z-40 touch-none pointer-events-none"></div>
        <div
          className={`regwall-wrapper regwall-content w-full max-h-[100vh] h-auto animate-fade-up animate-delay-300 animate-once no-scrollbar overflow-scroll flex flex-col md:flex-row items-center md:items-start justify-start md:justify-center md:gap-12 fixed z-50 bottom-0 left-0 bg-white ${styles['regwall-wrapper']}`}
        >
          {allowClose && setShowPaywall && (
            <button
              className="absolute top-2 right-4 text-black z-10"
              onClick={() => {
                setShowPaywall(false);
              }}
            >
              <FiX />
            </button>
          )}
          <div
            className={`${styles['optimizely-reg-paywall']} min-h-[285px] w-full flex flex-col items-center overflow-hidden bg-blue-950`}
          >
            <div className={styles['paywall-logo-wrapper']}>
              <EdgeLogoSvg fill="white" />
              <span>exclusive</span>
            </div>
            <div className={`${styles['content-wrapper']} flex-grow`}>
              <div className="paywall-text-section flex flex-col justify-center items-start">
                <span className={styles['paywall-subtitle']}>You have reached your free article limit</span>
                <div className={styles['paywall-title']}>Register for FREE to continue using Benzinga.com</div>
                <div className={styles['paywall-list-wrapper']}>
                  <div className={styles['paywall-list-heading']}>Free registration gives you access to:</div>
                  <ul className={styles['paywall-list']}>
                    {listItems.map((item, index) => (
                      <li className={styles['paywall-list-item']} key={index}>
                        <FaCheck className={styles['paywall-check-icon']} size={16} />
                        <div>{item}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className={styles['paywall-register-form']} id="register-form">
                <Register isRegwalled={true} onRegister={onRegister} />
                <button
                  className="text-gray-300 text-xs text-center paywall-login-redirect"
                  onClick={handleLoginRedirect}
                >
                  Already a member? <u>Log in</u>
                </button>
              </div>
            </div>
            <div className="absolute bottom-0 w-full flex flex-row justify-between">
              <Image
                alt="poly bear"
                className={styles['desktop-animal']}
                height={798}
                src="/next-assets/images/paywalls/poly-bear.png"
                unoptimized
                width={524}
              />
              <Image
                alt="poly bull"
                className={styles['desktop-animal']}
                height={798}
                src="/next-assets/images/paywalls/poly-bull.png"
                unoptimized
                width={524}
              />
            </div>
          </div>
        </div>
      </>
    );
  },
);

OptimizelyRegPaywall.displayName = 'OptimizelyRegPaywall';
